// Shared constants and utilities
import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send httpOnly auth cookie on every API request (web + WebView).
// Replaces the legacy `localStorage` Bearer-token flow — XSS-safe.
axios.defaults.withCredentials = true;

export const FUEL_TYPES = {
  diesel: { label: "Diesel", key: "diesel", color: "#1A1A1A" },
  unleaded_91: { label: "Unleaded 91", key: "unleaded_91", color: "#16A34A" },
  unleaded_95: { label: "Unleaded 95", key: "unleaded_95", color: "#DC2626" },
};

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: 2 }).format(value);

export const formatDate = (isoString) =>
  new Date(isoString).toLocaleDateString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

// Auth is now cookie-based (httpOnly). Kept for compatibility with callers that
// pass `authConfig()` into axios — returning an empty object means cookies still
// auto-attach via `axios.defaults.withCredentials = true`.
export const authConfig = () => ({});

// Mapbox token (still used for map rendering)
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// === GOOGLE-POWERED SEARCH (via backend proxy) ===

// Google Places Autocomplete — instant, high-quality NZ search
const searchGoogle = async (text) => {
  try {
    const resp = await fetch(`${API}/google/places/autocomplete?input=${encodeURIComponent(text)}`);
    const data = await resp.json();
    if (data.status !== "OK") return [];
    return (data.predictions || []).map(p => ({
      display_name: p.description,
      place_id: p.place_id,
      source: "google",
    }));
  } catch { return []; }
};

// Google Places Details — get coords from place_id
export const getPlaceDetails = async (placeId) => {
  try {
    const resp = await fetch(`${API}/google/places/details?place_id=${placeId}`);
    const data = await resp.json();
    if (data.status === "OK" && data.result?.geometry?.location) {
      const loc = data.result.geometry.location;
      return { lat: loc.lat, lng: loc.lng, display_name: data.result.formatted_address || data.result.name };
    }
    return null;
  } catch { return null; }
};

// Google Geocoding (forward: text -> coords) — fallback for typed addresses
export const geocodeAddress = async (text) => {
  try {
    const resp = await fetch(`${API}/google/geocode?address=${encodeURIComponent(text)}`);
    const data = await resp.json();
    if (data.status === "OK" && data.results?.length > 0) {
      const loc = data.results[0].geometry.location;
      return { lat: loc.lat, lng: loc.lng, display_name: data.results[0].formatted_address };
    }
    // Fallback to Mapbox geocoding
    const mbResp = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(text)}.json?country=nz&limit=1&access_token=${MAPBOX_TOKEN}`
    );
    const mbData = await mbResp.json();
    if (mbData.features?.length > 0) {
      const f = mbData.features[0];
      return { lat: f.center[1], lng: f.center[0], display_name: f.place_name };
    }
    return null;
  } catch { return null; }
};

// Mapbox reverse geocoding (coords -> text) — keep as-is, works great
export const reverseGeocode = async (lat, lng) => {
  try {
    const resp = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?limit=1&access_token=${MAPBOX_TOKEN}`
    );
    const data = await resp.json();
    if (data.features && data.features.length > 0) {
      return data.features[0].place_name.split(",").slice(0, 3).join(",").trim();
    }
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } catch { return `${lat.toFixed(4)}, ${lng.toFixed(4)}`; }
};

// Photon (OSM) POI search — finds campsites, tracks, lookouts, etc.
const searchPhoton = async (text) => {
  try {
    const resp = await fetch(
      `https://photon.komoot.io/api/?q=${encodeURIComponent(text)}&limit=5&lat=-41.0&lon=174.0&osm_tag=tourism&osm_tag=leisure&osm_tag=amenity&osm_tag=natural&osm_tag=highway:rest_area`
    );
    const data = await resp.json();
    return (data.features || []).map(f => {
      const p = f.properties || {};
      const coords = f.geometry?.coordinates || [];
      const nameParts = [p.name, p.city || p.county, p.state, p.country].filter(Boolean);
      return {
        display_name: nameParts.join(", "),
        lat: (coords[1] || 0).toString(),
        lon: (coords[0] || 0).toString(),
        source: "poi",
        poi_type: p.osm_value || p.osm_key || "",
      };
    }).filter(r => r.display_name && parseFloat(r.lat) !== 0);
  } catch { return []; }
};

// Hybrid search: Google Places + Photon POIs
export const searchPlaces = async (text) => {
  const [googleResults, photonResults] = await Promise.all([
    searchGoogle(text),
    searchPhoton(text),
  ]);
  return [...googleResults, ...photonResults].slice(0, 8);
};

// === GOOGLE DIRECTIONS ROUTE CALCULATION ===
import { decode } from "@googlemaps/polyline-codec";

export const calculateOSRMRoute = async (origin, destination, waypointCoords = []) => {
  try {
    const waypoints = waypointCoords.map(wp => `${wp.lat},${wp.lng}`);
    const resp = await fetch(`${API}/google/directions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        origin: `${origin.lat},${origin.lng}`,
        destination: `${destination.lat},${destination.lng}`,
        waypoints,
      }),
    });
    const data = await resp.json();
    if (data.status !== "OK" || !data.routes?.length) return null;
    // Convert Google routes to GeoJSON format (compatible with Mapbox rendering)
    return data.routes.map(route => {
      // Decode the overview polyline to GeoJSON coordinates
      const decoded = decode(route.overview_polyline.points);
      const coordinates = decoded.map(([lat, lng]) => [lng, lat]); // GeoJSON is [lng, lat]
      // Sum up legs for total distance/duration
      let totalDistance = 0, totalDuration = 0;
      for (const leg of route.legs) {
        totalDistance += leg.distance.value;
        totalDuration += leg.duration.value;
      }
      return {
        geometry: { type: "LineString", coordinates },
        distance: totalDistance,
        duration: totalDuration,
        legs: route.legs,
        summary: route.summary,
      };
    });
  } catch { return null; }
};

// Confetti burst
export const triggerConfetti = () => {
  const confetti = require("canvas-confetti");
  confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: ["#E52020", "#16A34A", "#DC2626"] });
};

// Format route distance/duration
export const formatRouteInfo = (route) => {
  const distanceKm = Math.round(route.distance / 1000 * 10) / 10;
  const totalSeconds = route.duration;
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.round((totalSeconds % 3600) / 60);
  const durationText = hours > 0 ? `${hours} hours ${mins} mins` : `${mins} mins`;
  return { distanceKm, durationText, totalSeconds };
};

// === RUC, TOLLS, FERRY ===

// RUC weight bands (NZD per 1,000 km) - 2-axle powered vehicles
export const RUC_WEIGHT_BANDS = [
  { key: "light", label: "Light (up to 3.5t)", rate: 76, maxWeight: 3500 },
  { key: "medium", label: "Medium (3.5t - 6t)", rate: 82, maxWeight: 6000 },
  { key: "heavy", label: "Heavy (6t - 9t)", rate: 167, maxWeight: 9000 },
  { key: "very_heavy", label: "Very Heavy (9t+)", rate: 352, maxWeight: 99999 },
];

// Default light vehicle rate
export const RUC_RATE_PER_1000KM = 76;

// NZ Toll roads with approximate route detection points
export const NZ_TOLLS = [
  {
    name: "Northern Gateway Toll Road",
    cost: 2.60,
    // SH1, Orewa/Puhoi to Warkworth (north of Auckland)
    points: [
      { lat: -36.587, lng: 174.697 }, // Puhoi
      { lat: -36.633, lng: 174.708 }, // Johnstones Hill tunnel
      { lat: -36.592, lng: 174.701 }, // midpoint
    ],
    detectRadius: 2, // km
  },
  {
    name: "Tauranga Eastern Link",
    cost: 2.30,
    // SH2, Paengaroa to Papamoa
    points: [
      { lat: -37.768, lng: 176.289 }, // Paengaroa
      { lat: -37.719, lng: 176.268 }, // Te Maunga
      { lat: -37.743, lng: 176.278 }, // midpoint
    ],
    detectRadius: 2,
  },
  {
    name: "Takitimu Drive",
    cost: 2.10,
    // SH29, Route K to Route J (Tauranga bypass to SH2/Port/Mt Maunganui)
    points: [
      { lat: -37.714, lng: 176.135 }, // Route K junction
      { lat: -37.698, lng: 176.183 }, // Route J junction
      { lat: -37.706, lng: 176.159 }, // midpoint
    ],
    detectRadius: 2,
  },
];

// Ferry crossing detection — Cook Strait
export const FERRY_DEFAULT_COST = 175;
// North Island southern tip / South Island northern tip
const NORTH_ISLAND_SOUTH = { lat: -41.29, lng: 174.78 }; // Wellington area
const SOUTH_ISLAND_NORTH = { lat: -41.27, lng: 174.04 }; // Picton area

// Haversine distance in km
const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Check if route polyline passes near a point
const routePassesNear = (routeCoords, point, radiusKm) => {
  // Sample every ~20th coordinate for performance
  const step = Math.max(1, Math.floor(routeCoords.length / 200));
  for (let i = 0; i < routeCoords.length; i += step) {
    const [lng, lat] = routeCoords[i]; // GeoJSON format [lng, lat]
    if (haversine(lat, lng, point.lat, point.lng) <= radiusKm) return true;
  }
  return false;
};

// Detect tolls on a route
export const detectTolls = (routeCoords) => {
  if (!routeCoords || routeCoords.length === 0) return [];
  return NZ_TOLLS.filter(toll =>
    toll.points.some(pt => routePassesNear(routeCoords, pt, toll.detectRadius))
  );
};

// === TOWING RESTRICTIONS ===
export const NZ_TOWING_ROADS = [
  {
    name: "Remutaka Hill Road",
    road: "SH2",
    warning: "Steep grades (10%+), sharp hairpins. Caution with long trailers.",
    points: [{ lat: -41.095, lng: 175.025 }, { lat: -41.070, lng: 175.060 }],
    detectRadius: 3,
  },
  {
    name: "Milford Road",
    road: "SH94",
    warning: "Homer Tunnel is single lane, narrow. Max vehicle height 4.0m. Avalanche risk in winter.",
    points: [{ lat: -44.760, lng: 167.980 }, { lat: -44.730, lng: 168.030 }],
    detectRadius: 4,
  },
  {
    name: "Crown Range Road",
    road: "Crown Range",
    warning: "NZ's highest sealed road. Steep 10%+ grades, tight switchbacks. Not recommended for long trailers.",
    points: [{ lat: -44.930, lng: 168.960 }, { lat: -44.960, lng: 169.010 }],
    detectRadius: 3,
  },
  {
    name: "Arthur's Pass",
    road: "SH73",
    warning: "Steep grades, Otira Viaduct. Caution with heavy/long combinations in winter.",
    points: [{ lat: -42.940, lng: 171.560 }, { lat: -42.920, lng: 171.580 }],
    detectRadius: 4,
  },
  {
    name: "Lewis Pass",
    road: "SH7",
    warning: "Winding mountain road with steep sections. Allow extra time when towing.",
    points: [{ lat: -42.370, lng: 172.400 }, { lat: -42.390, lng: 172.370 }],
    detectRadius: 4,
  },
  {
    name: "Lindis Pass",
    road: "SH8",
    warning: "Long steep gradients. Use low gear when descending with a trailer.",
    points: [{ lat: -44.595, lng: 169.640 }, { lat: -44.610, lng: 169.615 }],
    detectRadius: 4,
  },
  {
    name: "Haast Pass",
    road: "SH6",
    warning: "Remote, winding road with narrow sections. Limited services.",
    points: [{ lat: -44.100, lng: 169.370 }, { lat: -44.130, lng: 169.350 }],
    detectRadius: 4,
  },
];

export const detectTowingWarnings = (routeCoords) => {
  if (!routeCoords || routeCoords.length === 0) return [];
  return NZ_TOWING_ROADS.filter(road =>
    road.points.some(pt => routePassesNear(routeCoords, pt, road.detectRadius))
  );
};

// Detect if route crosses Cook Strait (origin/dest on different islands)
export const detectFerry = (originCoords, destCoords) => {
  if (!originCoords || !destCoords) return false;
  // Determine which island each coordinate is on
  const isNorthIsland = (coords) => {
    if (coords.lat > -40.8) return true; // definitely North Island
    if (coords.lat < -42.5) return false; // definitely South Island
    // Overlap zone (-40.8 to -42.5): North Island coast is east (lng > 174.4)
    // South Island coast (Picton/Nelson) is west (lng < 174.4)
    return coords.lng > 174.4;
  };
  return isNorthIsland(originCoords) !== isNorthIsland(destCoords);
};

// Calculate RUC cost (supports custom rate for different weight bands)
export const calculateRUC = (distanceKm, ratePerThousandKm = RUC_RATE_PER_1000KM) => {
  return Math.round((distanceKm / 1000) * ratePerThousandKm * 100) / 100;
};

// POI type labels and colors
export const POI_TYPES = {
  camp_site: { label: "Campsite", color: "#16A34A", icon: "tent" },
  viewpoint: { label: "Scenic Lookout", color: "#8B5CF6", icon: "eye" },
  fuel: { label: "Petrol Station", color: "#F59E0B", icon: "fuel" },
  toilets: { label: "Toilets", color: "#666666", icon: "droplet" },
  rest_area: { label: "Rest Stop", color: "#E52020", icon: "coffee" },
  hiking: { label: "Walk/Track", color: "#16A34A", icon: "trees" },
};
